/**
 * Records a picker can add on the spot, from the name typed into it: a form that needs
 * a department (or a supplier, a branch…) never sends the user off to make one first.
 * Modules register their own lists from their index.ts.
 */
export interface QuickCreate {
  /** Permission needed to add one. */
  perm: string;
  /** i18n key of the "+ Add «{{name}}»" action; the picker's filter can pick another. */
  label: (filter?: Record<string, string>) => string;
  /** The new record, from the name typed and the picker's filter (e.g. is_supplier). */
  body: (name: string, filter?: Record<string, string>) => Record<string, unknown>;
}

const registry = new Map<string, QuickCreate>();

export function registerQuickCreate(path: string, q: QuickCreate) {
  registry.set(path, q);
}

export function quickCreateFor(path: string): QuickCreate | undefined {
  return registry.get(path);
}

// The directory's lists, which every app picks from.
registerQuickCreate("/v1/core/departments", { perm: "core.department.create", label: () => "quick.department", body: (name) => ({ name }) });
registerQuickCreate("/v1/core/branches", { perm: "core.branch.create", label: () => "quick.branch", body: (name) => ({ name }) });
registerQuickCreate("/v1/core/organizations", {
  perm: "core.organization.create",
  label: (f) => (f?.is_supplier === "true" ? "quick.supplier" : "quick.customer"),
  body: (name, f) => (f?.is_supplier === "true" ? { name, is_supplier: true } : { name, is_customer: true }),
});
registerQuickCreate("/v1/core/contacts", {
  perm: "core.contact.create",
  label: () => "quick.contact",
  body: (name, f) => {
    const [first, ...rest] = name.split(/\s+/);
    return { first_name: first, last_name: rest.join(" "), ...(f?.organization_id ? { organization_id: f.organization_id } : {}) };
  },
});
